import { FC, ReactNode, SyntheticEvent, useState } from 'react'
import styled from 'styled-components'

/**
 * Single localStorage key holding every section's open/closed state as a
 * `Record<sectionKey, boolean>`, following the `gecko.*` naming convention
 * from `chartLocalState.ts`. One key (rather than one per section) keeps the
 * lab's footprint tidy and makes the whole record trivially inspectable.
 */
const SECTIONS_STORAGE_KEY = 'gecko.experimentLab.sections'

const readSections = (): Record<string, boolean> => {
  try {
    const raw = window.localStorage.getItem(SECTIONS_STORAGE_KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {}
    }
    return parsed as Record<string, boolean>
  } catch {
    // Unparseable JSON or storage unavailable (sandboxed iframe, disabled
    // cookies) — fall back to defaults.
    return {}
  }
}

const writeSection = (sectionKey: string, open: boolean): void => {
  try {
    const next = { ...readSections(), [sectionKey]: open }
    window.localStorage.setItem(SECTIONS_STORAGE_KEY, JSON.stringify(next))
  } catch {
    // Quota exceeded / private mode — persistence is best-effort.
  }
}

const Details = styled.details`
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 8px;
  padding: 0.55rem 0.75rem;
  background: ${({ theme }) => theme.forecast.background2};

  & > summary {
    cursor: pointer;
    font-size: 0.78rem;
    font-weight: 600;
    color: ${({ theme }) => theme.forecast.secondaryText};
  }
`

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.5rem;
`

export interface CollapsibleSectionProps {
  /** Stable identifier the open/closed state persists under. */
  sectionKey: string
  title: ReactNode
  /** Initial state when nothing is stored for this section yet. */
  defaultOpen?: boolean
  children: ReactNode
}

/**
 * The Experiment tab's collapsible section: the styled `<details>` pattern
 * formerly copy-pasted for "Saved experiments" and the advanced cleaning
 * JSON, extracted and given per-section localStorage persistence so a
 * curated layout (e.g. cleaning open, diagnostics closed) survives reloads.
 */
export const CollapsibleSection: FC<CollapsibleSectionProps> = ({
  sectionKey,
  title,
  defaultOpen = true,
  children,
}) => {
  const [open, setOpen] = useState(
    () => readSections()[sectionKey] ?? defaultOpen
  )

  const onToggle = (event: SyntheticEvent<HTMLDetailsElement>) => {
    const isOpen = event.currentTarget.open
    // <details> fires toggle on mount in some browsers; only persist real
    // user-driven changes.
    if (isOpen === open) return
    setOpen(isOpen)
    writeSection(sectionKey, isOpen)
  }

  return (
    <Details open={open} onToggle={onToggle}>
      <summary>{title}</summary>
      <Body>{children}</Body>
    </Details>
  )
}
